class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (typeof trapFocus === 'function' && containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.open();
      this.bindVioraSubscriptionToggles();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindVioraSubscriptionToggles() {
    document.querySelectorAll('[data-viora-subscription-toggle]').forEach((toggle) => {
      if (toggle.dataset.vioraBound === 'true') return;
      toggle.dataset.vioraBound = 'true';

      toggle.addEventListener('change', async (event) => {
        const input = event.currentTarget;
        const line = input.dataset.line;
        const sellingPlanId = input.dataset.sellingPlanId;
        const shouldSubscribe = input.checked;

        if (!line || !sellingPlanId) {
          console.warn('Missing cart line or selling plan ID for subscription toggle.');
          return;
        }

        input.disabled = true;

        try {
          await this.updateCartLineSellingPlan({
            line: line,
            sellingPlan: shouldSubscribe ? sellingPlanId : '',
          });

          await this.refreshCartDrawer();
        } catch (error) {
          console.error('Subscription toggle error:', error);
          input.checked = !shouldSubscribe;
        } finally {
          input.disabled = false;
        }
      });
    });
  }

  async updateCartLineSellingPlan({ line, sellingPlan }) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line: Number(line),
        selling_plan: sellingPlan,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart selling plan.');
    }

    return response.json();
  }

  async refreshCartDrawer() {
    const response = await fetch('/?section_id=cart-drawer');

    if (!response.ok) {
      throw new Error('Failed to refresh cart drawer.');
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newDrawer = doc.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');

    if (newDrawer && currentDrawer) {
      currentDrawer.innerHTML = newDrawer.innerHTML;
      currentDrawer.classList.remove('is-empty');
      currentDrawer.classList.add('animate', 'active');

      const overlay = currentDrawer.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', currentDrawer.close.bind(currentDrawer));
      }

      currentDrawer.bindVioraSubscriptionToggles();
    }

    const newCartIcon = doc.querySelector('#cart-icon-bubble');
    const currentCartIcon = document.querySelector('#cart-icon-bubble');

    if (newCartIcon && currentCartIcon) {
      currentCartIcon.innerHTML = newCartIcon.innerHTML;
    }
  }

  connectedCallback() {
    this.bindVioraSubscriptionToggles();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}